/**
 * Legacy shim — re-exports from services/ so existing component imports continue to work.
 * Components should import directly from services/ in new code.
 */
export { apiFetch } from "../services/api.js";
export { sendMessage as sendChat } from "../services/chat.js";

// ── Persona detail generation (admin only) ────────────────────────────────────
import { apiFetch } from "../services/api.js";

export async function generatePersonaDetails(statement) {
  try {
    return await apiFetch("/api/admin/generate-persona-details", {
      method: "POST",
      body: JSON.stringify({ statement }),
    });
  } catch {
    return {
      tagline: "Serving the people with integrity",
      suggestedPrompts: [
        "What are your top priorities?",
        "How will you handle the economy?",
        "What is your stance on healthcare?",
        "How will you improve education?",
      ],
      keyPolicies: ["Economic growth", "Healthcare access", "Public safety"],
    };
  }
}

// ── Trivia completion (v1.28.3) ──────────────────────────────────────────────
// Closes a trivia round so the server can re-score from the stored answer
// key (not the client-submitted value) and either addXp or refund the play.
// `answers` is an array of the voter's picked option indexes per question;
// `roundToken` is the id returned by generateTriviaQuestions. Returns the
// parsed response (null on any error so the caller falls back to its own
// reconciliation path).
export async function completeTriviaRound(personaId, roundToken, answers) {
  try {
    return await apiFetch(`/api/trivia/${personaId}/complete`, {
      method: "POST",
      body: JSON.stringify({ roundToken, answers }),
    });
  } catch {
    return null;
  }
}

// ── Trivia generation ─────────────────────────────────────────────────────────
// v1.28.3 — response shape changed from `Question[]` to `{ questions, roundToken }`.
// The token is required by /complete to prevent replay + server-authoritative
// scoring (see migration 039 + routes/trivia.ts).
export async function generateTriviaQuestions(persona, voterPhone) {
  try {
    const data = await apiFetch(`/api/trivia/${persona.id}`, {
      method: "POST",
      body: JSON.stringify({ phone: voterPhone }),
    });
    // Be tolerant of both the new object shape and a future/old array
    // shape — simpler than a version flag and lets us evolve the server
    // response without breaking cached clients catastrophically.
    if (Array.isArray(data)) {
      return { questions: data.slice(0, 5), roundToken: null };
    }
    return {
      questions: Array.isArray(data?.questions) ? data.questions.slice(0, 5) : [],
      roundToken: typeof data?.roundToken === "string" ? data.roundToken : null,
    };
  } catch {
    // Offline / failure fallback — questions with correct=0 and NO
    // roundToken. Client-side scoring is unavoidable in this degraded
    // path since /complete won't accept a null token; but since the
    // user is offline anyway, /complete won't be callable and no XP
    // is minted. UX-wise they just see the round locally.
    const questions = [
      { q: `What is ${persona.name}'s current political role?`, options: [persona.role, "State Governor", "National Assembly Member", "Cabinet Minister"], correct: 0, difficulty: "easy" },
      { q: `${persona.name} is based in which location?`, options: [persona.location || "Abuja", "Lagos", "Kano", "Port Harcourt"], correct: 0, difficulty: "medium" },
      { q: `Which of these is a key policy of ${persona.name}?`, options: [persona.keyPolicies?.[0] || "Economic reform", "Military expansion", "Privatisation", "Deregulation"], correct: 0, difficulty: "medium" },
      { q: `What phrase best describes ${persona.name}'s governance philosophy?`, options: [persona.tagline || "Reform and progress", "Status quo", "Austerity first", "Open borders"], correct: 0, difficulty: "hard" },
    ];
    return { questions, roundToken: null };
  }
}
